(() => {
  if (customElements.get('earthen-subscription-widget')) return;
  const labels = { hours_2:'2 hours', days_15:'15 days', days_30:'30 days', days_45:'45 days', days_60:'60 days' };
  class EarthenSubscriptionWidget extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
      try {
        const response = await fetch(`/apps/subscriptions/config?product_id=${encodeURIComponent(this.dataset.productId)}`, {credentials:'same-origin'});
        this.config = await response.json();
        if (!this.config.ok || !this.config.enabled) return;
        // Discounts come only from the app (owned by the loyalty programme); no local copy.
        this.intervalDiscounts = this.config.intervalDiscountsBps || {};
        this.intervalLabels = {...labels, ...(this.config.intervalLabels || {})};
        const interval = this.querySelector('[data-interval]');
        interval.innerHTML = this.config.intervals.map(value => `<option value="${this.escape(value)}">${this.escape(this.intervalLabels[value] || value)}</option>`).join('');
        if (this.config.intervals.includes('days_30')) interval.value = 'days_30';
        interval.addEventListener('change', () => this.renderDiscount());
        // One delivery frequency per cart: a mixed cart cannot be activated as
        // one subscription, so the second item follows the first.
        await this.alignWithCart();
        // Nothing about a subscription happens until the customer picks
        // "Subscribe": the radios decide what the one Add to Cart button does.
        const toggle = this.querySelector('[data-toggle]');
        const once = this.querySelector('[data-purchase-once]');
        const sync = () => {
          const subscribing = toggle.checked;
          this.querySelector('[data-controls]').hidden = !subscribing;
          this.classList.toggle('es-pt--subscribing', subscribing);
          const atc = this.atcButton();
          if (atc && !atc.disabled) atc.textContent = subscribing ? this.subscribeLabel() : 'Add to Cart';
          this.feedback('');
        };
        toggle.addEventListener('change', sync);
        if (once) once.addEventListener('change', sync);
        // Arriving from a shop-card "Subscribe" pill: land with Subscribe
        // selected so the frequency choice is the next thing on screen.
        if (new URLSearchParams(location.search).get('subscribe') === '1') {
          toggle.checked = true;
          sync();
        }
        const form = this.closest('form');
        if (form) {
          form.addEventListener('submit', (event) => {
            if (!toggle.checked) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.addToCart();
          }, true);
        }
        this.renderDiscount();
        this.hidden = false;
      } catch (_) { /* Signup remains safely hidden when configuration is unavailable. */ }
    }
    selectedVariant() {
      const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
      const id = String(idInput?.value || this.product.selected_or_first_available_variant?.id || '');
      return this.product.variants.find(variant => String(variant.id) === id);
    }
    atcButton() {
      const form = this.closest('form');
      return form ? form.querySelector('button[name="add"], .es-atc-btn') : null;
    }
    subscribeLabel() {
      const code = this.querySelector('[data-interval]').value;
      return `Subscribe: every ${this.intervalLabels[code] || code}`;
    }
    // Returns the frequency already in the cart, if any subscription item is there.
    async cartInterval() {
      try {
        const cart = await fetch('/cart.js', {credentials:'same-origin'}).then(response => response.json());
        const line = (cart.items || []).find(item => item.properties && item.properties._earthen_subscription_interval);
        return line ? String(line.properties._earthen_subscription_interval) : null;
      } catch (_) { return null; }
    }
    async alignWithCart() {
      const existing = await this.cartInterval();
      const interval = this.querySelector('[data-interval]');
      const note = this.querySelector('[data-interval-note]');
      if (!existing || !this.config.intervals.includes(existing)) {
        interval.disabled = false;
        if (note) note.hidden = true;
        return;
      }
      interval.value = existing;
      interval.disabled = true;
      if (note) {
        note.textContent = `Your cart already has deliveries every ${this.intervalLabels[existing] || existing}, so this item joins that schedule. You can change the frequency for all items from your account later.`;
        note.hidden = false;
      }
      this.renderDiscount();
    }
    async addToCart() {
      const variant = this.selectedVariant();
      const form = this.closest('form');
      const qtyInput = form ? form.querySelector('[name="quantity"]') : null;
      const quantity = Math.max(1, Number((qtyInput && qtyInput.value) || 1));
      if (!variant || !variant.available) return this.feedback('This variant is currently unavailable.');
      const button = this.atcButton() || this;
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding...';
      try {
        // Re-check at submit time: another tab may have added a subscription since the page loaded.
        const existing = await this.cartInterval();
        const select = this.querySelector('[data-interval]');
        if (existing && this.config.intervals.includes(existing) && select.value !== existing) {
          select.value = existing;
          await this.alignWithCart();
        }
        const intervalCode = select.value;
        const line = {
        productId: String(this.product.id), variantId: String(variant.id), sku: variant.sku || null,
        productTitle: this.product.title, variantTitle: variant.title, quantity,
        unitPricePaise: Number(variant.price)
        };
        const response = await fetch('/apps/subscriptions/intent', {
          method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({intervalCode, lines:[line]})
        });
        const result = await response.json();
        if (!response.ok || !result.ok) throw new Error(result.error || 'Could not start subscription checkout');
        const cart = await fetch('/cart/add.js', {
          method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({items:[{
            id:line.variantId, quantity:line.quantity,
            properties:{
              '_earthen_subscription_intent':result.signedCartReference,
              '_earthen_subscription_interval':intervalCode,
              'Purchase option':`Subscription - ${this.formatPercent(this.discountFor(intervalCode))} off every repeat delivery`,
              'Delivery frequency':`Every ${this.intervalLabels[intervalCode] || intervalCode}`
            }
          }]})
        });
        if (!cart.ok) throw new Error('Shopify could not add this subscription to cart');
        await this.rememberSubscriptionIntent(result.signedCartReference);
        this.markSubscriptionCheckoutPending();
        button.textContent = 'Added as a subscription';
        this.feedback('');
        await this.refreshCart();
        window.setTimeout(() => { button.disabled = false; button.textContent = this.subscribeLabel(); }, 2500);
      } catch (error) {
        button.disabled = false;
        button.textContent = original;
        this.feedback(error.message || 'Please try again.');
      }
    }
    stepQuantity(delta) {
      const input = this.querySelector('[data-quantity]');
      const next = Math.min(100, Math.max(1, Number(input.value || 1) + delta));
      input.value = String(next);
    }
    renderDiscount() {
      const intervalCode = this.querySelector('[data-interval]').value;
      const percent = this.formatPercent(this.discountFor(intervalCode));
      this.querySelector('[data-discount]').textContent = percent;
      this.querySelector('[data-saving-copy]').textContent = `Today's order is at the store price. Every repeat delivery, every ${this.intervalLabels[intervalCode] || intervalCode}, is ${percent} off the store price on the day, shown as a discount on the order. Cancel anytime.`;
      const atc = this.atcButton();
      if (atc && this.querySelector('[data-toggle]').checked && !atc.disabled) atc.textContent = this.subscribeLabel();
    }
    discountFor(intervalCode) {
      return Number(this.intervalDiscounts?.[intervalCode] ?? 0);
    }
    async refreshCart() {
      try {
        const cart = await fetch('/cart.js', {credentials:'same-origin'}).then(response => response.json());
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles:true,
          detail:{
            resource:cart,
            sourceId:'earthen-subscription-widget',
            data:{
              source:'earthen-subscription-widget',
              itemCount:cart.item_count
            }
          }
        }));
      } catch (_) { this.feedback(''); }
    }
    async rememberSubscriptionIntent(reference) {
      // The attribute mirrors the subscription lines actually in the cart, so
      // removed or re-picked items do not leave stale references behind.
      const attributeKey = '_earthen_subscription_intents';
      let references = [];
      try {
        const cart = await fetch('/cart.js', {credentials:'same-origin'}).then(response => response.json());
        references = (cart.items || [])
          .map(item => item.properties && item.properties._earthen_subscription_intent)
          .filter(value => typeof value === 'string' && value.length > 0);
      } catch (_) {
        references = [];
      }
      if (!references.includes(reference)) references.push(reference);
      await fetch('/cart/update.js', {
        method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({attributes:{[attributeKey]:JSON.stringify(references)}})
      });
    }
    feedback(message){ this.querySelector('[data-feedback]').textContent=message; }
    markSubscriptionCheckoutPending(){
      try {
        window.localStorage.setItem('esSubscriptionCheckoutPending', JSON.stringify({expiresAt:Date.now()+2*60*60*1000}));
      } catch (_) {}
    }
    formatPercent(bps){ return `${String(Number(bps)/100)}%`; }
    escape(value){ return String(value).replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char])); }
  }
  customElements.define('earthen-subscription-widget', EarthenSubscriptionWidget);
})();
